/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-07-24
 */

import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { RootState } from '../store';
import { API_FULL_BASE_URL } from '../config/api';

/**
 * Determine whether a request needs CSRF protection.
 *
 * Drives the decision off the actual HTTP method only — never the endpoint
 * name. RTK Query mutations always pass a `method` in the args object; GET
 * queries don't need CSRF. The earlier substring-on-endpoint-name heuristic
 * silently dropped CSRF on any future mutation whose name didn't match the
 * keyword list (e.g. `markRead`, `flag`, `complete`, `save`).
 */
const needsCSRFToken = (args: any): boolean => {
  if (typeof args === 'object' && args !== null && args.method) {
    const method = args.method.toUpperCase();
    return ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);
  }
  return false;
};

/**
 * Enhanced base query with session-based authentication support.
 *
 * Features:
 * - Automatic cookie handling with credentials: 'include'
 * - CSRF token injection for state-changing requests
 * - Session-based authentication (no JWT tokens)
 * - Automatic error handling for authentication failures
 */
export const baseQuery = fetchBaseQuery({
  baseUrl: API_FULL_BASE_URL,
  credentials: 'include', // Send cookies with all requests for session auth
  prepareHeaders: (headers, api) => {
    const state = api.getState() as RootState;
    const { csrfToken } = state.auth;

    // Determine if this request needs CSRF protection
    const needsCSRF = needsCSRFToken(api.arg);

    // Add CSRF token for state-changing requests
    if (needsCSRF && csrfToken) {
      headers.set('X-CSRFToken', csrfToken);

      // Debug logging in development
      if (process.env.NODE_ENV === 'development') {
        console.debug(`[CSRF] Adding token for request:`, {
          endpoint: api.endpoint || 'unknown',
          hasToken: !!csrfToken,
          tokenPreview: csrfToken ? `${csrfToken.substring(0, 8)}...` : 'none',
        });
      }
    } else if (needsCSRF && !csrfToken) {
      // Warning for missing CSRF token on state-changing requests. Log the
      // URL only: the body can hold personal data or a password.
      console.warn(`[CSRF] Missing CSRF token for request:`, {
        endpoint: api.endpoint || 'unknown',
        url: typeof api.arg === 'string' ? api.arg : api.arg?.url || 'unknown',
      });
    }

    // Set content type for JSON requests (but don't override FormData)
    if (!headers.has('content-type')) {
      // Check if the request body is FormData
      const isFormData =
        typeof api.arg === 'object' &&
        api.arg !== null &&
        api.arg.body instanceof FormData;

      if (!isFormData) {
        headers.set('content-type', 'application/json');
      }
    }

    return headers;
  },
});

/**
 * Enhanced base query with automatic CSRF token management.
 *
 * Features:
 * - Automatically fetches CSRF token for state-changing requests if missing
 * - Retries requests with fresh CSRF token on 401/403 errors
 * - Provides detailed logging for debugging
 */
export const baseQueryWithReauth = async (
  args: any,
  api: any,
  extraOptions: any
) => {
  const state = api.getState() as any;
  const { csrfToken } = state.auth;

  // Use the same CSRF detection logic as prepareHeaders
  const needsCSRF = needsCSRFToken(args);

  // If we need CSRF but don't have a token, fetch one first
  if (needsCSRF && !csrfToken) {
    if (process.env.NODE_ENV === 'development') {
      console.debug(`[CSRF] Fetching token for request - no token available`, {
        endpoint: api.endpoint || 'unknown',
        args: typeof args === 'string' ? args : args?.url || 'unknown',
      });
    }

    try {
      const csrfResult = await baseQuery(
        { url: 'auth/csrf/', method: 'GET' },
        api,
        extraOptions
      );

      if (csrfResult.data) {
        const csrfData = csrfResult.data as { csrf_token: string };
        api.dispatch({
          type: 'auth/updateCsrfToken',
          payload: csrfData.csrf_token,
        });

        if (process.env.NODE_ENV === 'development') {
          console.debug(
            `[CSRF] Successfully fetched token: ${csrfData.csrf_token.substring(0, 8)}...`
          );
        }
      }
    } catch (error) {
      console.warn('[CSRF] Failed to fetch CSRF token:', error);
    }
  }

  // Make the original request
  let result = await baseQuery(args, api, extraOptions);

  // Handle authentication/CSRF errors and subscription limit errors with retry logic
  if (
    result.error &&
    (result.error.status === 401 ||
      result.error.status === 402 ||
      result.error.status === 403)
  ) {
    const errorData = result.error.data as any;

    // Check if it's a CSRF-related error
    const isCSRFError =
      errorData?.detail?.includes('CSRF') ||
      errorData?.error?.includes('CSRF') ||
      errorData?.detail?.includes('Forbidden') ||
      errorData?.detail?.includes('token') ||
      (result.error.status === 403 && needsCSRF);

    if (isCSRFError) {
      if (process.env.NODE_ENV === 'development') {
        console.debug(
          `[CSRF] ${result.error.status} error detected, fetching fresh token`,
          {
            endpoint: api.endpoint || 'unknown',
            errorDetail: errorData?.detail,
          }
        );
      }

      try {
        // Try to fetch a new CSRF token
        const csrfResult = await baseQuery(
          { url: 'auth/csrf/', method: 'GET' },
          api,
          extraOptions
        );

        if (csrfResult.data) {
          const csrfData = csrfResult.data as { csrf_token: string };

          // Update the CSRF token in the store
          api.dispatch({
            type: 'auth/updateCsrfToken',
            payload: csrfData.csrf_token,
          });

          if (process.env.NODE_ENV === 'development') {
            console.debug(
              `[CSRF] Retrying request with fresh token: ${csrfData.csrf_token.substring(0, 8)}...`
            );
          }

          // Retry the original request with the new token
          result = await baseQuery(args, api, extraOptions);
        }
      } catch (error) {
        console.warn(
          '[CSRF] Failed to fetch fresh CSRF token for retry:',
          error
        );
      }
    } else if (result.error.status === 402) {
      // 402 Payment Required - subscription limit reached
      if (process.env.NODE_ENV === 'development') {
        console.debug('[SUBSCRIPTION] 402 error - subscription limit reached', {
          endpoint: api.endpoint || 'unknown',
          errorDetail: errorData?.detail,
          currentUsage: errorData?.current_usage,
          upgradeSuggestions: errorData?.upgrade_suggestions,
        });
      }

      // Don't retry 402 errors - these need user intervention (upgrade)
      // The error will be handled by the component to show upgrade prompts
    } else if (result.error.status === 401) {
      // 401 without CSRF issues might mean session expired
      if (process.env.NODE_ENV === 'development') {
        console.debug('[AUTH] 401 error - session may be expired');
      }

      // Clear auth state on 401 errors
      api.dispatch({ type: 'auth/logout' });
    }
  }

  return result;
};
